import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";
import AdaptiveLoopHeader from "@/components/adaptive-loop/AdaptiveLoopHeader";

/**
 * ProgramTemplate — shared shell for Program pages.
 * Programs are implementation pathways for the Adaptive Physiology methodology.
 * The methodology is the product. Programs are levels of support around it.
 */
const ProgramTemplate = ({
  pageKey,
  breadcrumbLabel,
  overline,
  headline,
  headlineAccent,
  audience, // 1-line clarifier under headline
  lead,
  whyExists, // { title, paragraphs: [] }
  howApplied, // { title, intro, steps: [ { title, body }, ...4 ] }
  experience, // { title, intro, capacities: [ { label, body }, ... ] }
  designedFor, // { title, groups: [ { title, body }, ... ] }
  nextSteps, // { primary: { label, to }, secondary?: { label, to } }
}) => {
  return (
    <PageShell>
      {/* Hero */}
      <section
        data-testid={`program-${pageKey}-hero`}
        className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 border-b border-line-soft overflow-hidden forr-canvas"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-70 pointer-events-none" />
        <div className="absolute top-40 right-[-200px] w-[500px] h-[500px] forr-radial-warm opacity-30 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <nav
              aria-label="Breadcrumb"
              data-testid={`program-${pageKey}-breadcrumb`}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-10"
            >
              <Link to="/" className="hover:forr-text-teal-deep transition-colors">Home</Link>
              <span className="forr-text-faint">›</span>
              <Link to="/programs" className="hover:forr-text-teal-deep transition-colors">Programs</Link>
              <span className="forr-text-faint">›</span>
              <span className="forr-text-soft">{breadcrumbLabel}</span>
            </nav>
          </Reveal>

          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <span className="forr-hairline" />
                  <span className="forr-overline">{overline}</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  data-testid={`program-${pageKey}-headline`}
                  className="font-display forr-text-ink text-[36px] sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.04] font-medium tracking-[-0.03em]"
                >
                  {headline}
                  {headlineAccent && (
                    <>
                      {" "}
                      <span className="forr-text-teal-deep">{headlineAccent}</span>
                    </>
                  )}
                </h1>
              </Reveal>
              {audience && (
                <Reveal delay={0.1}>
                  <p
                    data-testid={`program-${pageKey}-audience`}
                    className="mt-6 max-w-2xl text-sm sm:text-base font-medium uppercase tracking-[0.18em] forr-text-teal-deep"
                  >
                    {audience}
                  </p>
                </Reveal>
              )}
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-2xl text-base sm:text-lg font-light forr-text-soft leading-relaxed">
                  {lead}
                </p>
              </Reveal>
            </div>

            <div className="col-span-12 lg:col-span-5 flex justify-center lg:justify-end">
              <Reveal delay={0.1} className="w-full max-w-[350px]">
                <AdaptiveLoopHeader
                  testId={`program-${pageKey}-loop`}
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 01 · Why This Exists */}
      <section
        data-testid={`program-${pageKey}-why`}
        className="relative py-24 sm:py-32 forr-canvas-alt"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="01"
                  label="Why This Exists"
                  testId={`program-${pageKey}-why-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {whyExists.title}
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div className="space-y-6">
                {whyExists.paragraphs.map((p, i) => (
                  <Reveal delay={0.1 + i * 0.05} key={i}>
                    <p className="text-lg font-light forr-text-soft leading-relaxed">
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 · How Adaptive Physiology Is Applied */}
      <section
        data-testid={`program-${pageKey}-how`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="02"
              label="How Adaptive Physiology Is Applied"
              testId={`program-${pageKey}-how-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              {howApplied.title}
            </h2>
          </Reveal>
          {howApplied.intro && (
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
                {howApplied.intro}
              </p>
            </Reveal>
          )}

          {/* Reference the framework */}
          <Reveal delay={0.15}>
            <div
              data-testid={`program-${pageKey}-loop-reference`}
              className="mt-12 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep"
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: "var(--forr-teal)" }}
              />
              The Adaptive Loop™ · Assess → Prescribe → Reinforce → Retrain
            </div>
          </Reveal>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {howApplied.steps.map((s, i) => (
              <Reveal delay={i * 0.06} key={s.title}>
                <div
                  data-testid={`program-${pageKey}-step-${i + 1}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-display forr-text-teal-deep text-2xl font-medium tracking-tight">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="forr-overline">
                      {["Assess", "Prescribe", "Reinforce", "Retrain"][i]}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 · What Participants Experience */}
      <section
        data-testid={`program-${pageKey}-experience`}
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="03"
                  label="What Participants Experience"
                  testId={`program-${pageKey}-experience-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {experience.title}
                </h2>
              </Reveal>
              {experience.intro && (
                <Reveal delay={0.15}>
                  <p className="mt-8 max-w-md text-base font-light forr-text-soft leading-relaxed">
                    {experience.intro}
                  </p>
                </Reveal>
              )}
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <ul className="space-y-4">
                {experience.capacities.map((c, i) => (
                  <Reveal delay={i * 0.05} key={c.label}>
                    <li
                      data-testid={`program-${pageKey}-capacity-${i + 1}`}
                      className="flex items-start gap-5 border-b border-line-soft pb-5 last:border-0"
                    >
                      <div
                        className="w-8 h-8 rounded-xl shrink-0 flex items-center justify-center mt-1"
                        style={{
                          backgroundColor: "var(--forr-teal-tint)",
                          color: "var(--forr-teal-deep)",
                        }}
                      >
                        <Check size={14} strokeWidth={1.8} />
                      </div>
                      <div>
                        <div className="font-display forr-text-ink text-lg font-medium tracking-tight">
                          {c.label}
                        </div>
                        <div className="mt-1 text-sm forr-text-soft font-light leading-relaxed">
                          {c.body}
                        </div>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 04 · Who This Is Designed For */}
      <section
        data-testid={`program-${pageKey}-designed-for`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="04"
              label="Who This Is Designed For"
              testId={`program-${pageKey}-designed-for-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              {designedFor.title}
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {designedFor.groups.map((g, i) => (
              <Reveal delay={i * 0.05} key={g.title}>
                <div
                  data-testid={`program-${pageKey}-audience-${i + 1}`}
                  className="rounded-[20px] p-6 forr-surface h-full"
                  style={{ border: "1px solid var(--forr-line-soft)" }}
                >
                  <div className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep mb-3">
                    Fit
                  </div>
                  <h3 className="font-display forr-text-ink text-base font-medium tracking-tight">
                    {g.title}
                  </h3>
                  <p className="mt-2 text-sm forr-text-soft font-light leading-relaxed">
                    {g.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 · Next Steps */}
      <section
        data-testid={`program-${pageKey}-next-steps`}
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="05"
                  label="Next Steps"
                  testId={`program-${pageKey}-next-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Begin a conversation.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md text-base font-light forr-text-soft leading-relaxed">
                  Fit and readiness are reviewed individually. The first step is a
                  conversation — not a purchase.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8 flex flex-col justify-center gap-4">
              <Reveal delay={0.1}>
                <Link
                  to={nextSteps.primary.to}
                  data-testid={`program-${pageKey}-next-primary`}
                  className="forr-card rounded-[24px] p-8 block group"
                >
                  <div className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep mb-3">
                    Primary
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <div>
                      <div className="font-display forr-text-ink text-xl font-medium tracking-tight">
                        {nextSteps.primary.label}
                      </div>
                      {nextSteps.primary.desc && (
                        <div className="mt-2 text-sm forr-text-soft font-light leading-relaxed">
                          {nextSteps.primary.desc}
                        </div>
                      )}
                    </div>
                    <ArrowRight
                      size={20}
                      strokeWidth={1.75}
                      className="forr-text-teal-deep transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
              {nextSteps.secondary && (
                <Reveal delay={0.15}>
                  <Link
                    to={nextSteps.secondary.to}
                    data-testid={`program-${pageKey}-next-secondary`}
                    className="rounded-[24px] p-8 block group forr-canvas-warm"
                    style={{ border: "1px solid var(--forr-line-soft)" }}
                  >
                    <div className="text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-3">
                      Or
                    </div>
                    <div className="flex items-center justify-between gap-6">
                      <div>
                        <div className="font-display forr-text-ink text-lg font-medium tracking-tight">
                          {nextSteps.secondary.label}
                        </div>
                        {nextSteps.secondary.desc && (
                          <div className="mt-2 text-sm forr-text-soft font-light leading-relaxed">
                            {nextSteps.secondary.desc}
                          </div>
                        )}
                      </div>
                      <ArrowRight
                        size={18}
                        strokeWidth={1.75}
                        className="forr-text-soft transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </div>
                  </Link>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      <NextStep
        overline="Return"
        title="Compare all four pathways"
        description="Every program follows the same Adaptive Physiology™ framework. Choose the level of support that matches your context."
        to="/programs"
        cta="Back to Programs"
        testId={`program-${pageKey}-back`}
      />
    </PageShell>
  );
};

export default ProgramTemplate;
